import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { OptionChangedEvent } from 'devextreme/ui/accordion';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	delay,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';

import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	bulkDeleteDepotsFail,
	bulkDeleteDepotsSuccess,
	deleteDepotFail,
	deleteDepotSuccess,
	DepotSelector,
	initDepotList,
	navigateToDepot,
	navigateToDepotCreate,
	navigateToDepotCreateModal,
	navigateToDepotDuplicate,
	refreshDepotList,
	resetAvailableHeaders,
	toggleDepotStatus,
	updateEntitiesCountForDepot,
} from '../../store';
import { CreateEntityFromDepotModalComponent } from '../create-entity-from-depot-modal/create-entity-from-depot-modal.component';
import {
	DepotAction,
	DepotActionPopupComponent,
	IDepotActionModalData,
} from '../depot-action-popup/depot-action-popup.component';
import { DepotItemEntity } from './depot-item.entity';
import { DepotListEntity } from './depot-list.entity';

type OptionChangeAction = 'page-change' | 'pageSize-change' | 'sort' | 'filter';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

@Component({
	selector: 'app-depot-list',
	templateUrl: './depot-list.component.html',
	styleUrls: ['./depot-list.component.scss'],
	inputs: ['projectLabel'],
})
export class DepotListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@depot.list.action.duplicate:`,
			action: (item: unknown): void => {
				const depot = item as DepotItemEntity;
				this.duplicateDepot(depot);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const depot = item as DepotItemEntity;
				this.toggleActivationStatus(depot);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@depot.list.action.duplicate:`,
			action: (item: unknown): void => {
				const depot = item as DepotItemEntity;
				this.duplicateDepot(depot);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const depot = item as DepotItemEntity;
				this.toggleActivationStatus(depot);
			},
			icon: ActionIcons.ActivateElement,
		},
	];

	public isImportedHeaderFilter = [
		{
			text: $localize`:i18n=@@depot.list.column.imported:`,
			value: ['isImported', '=', true],
		},
		{
			text: $localize`:i18n=@@depot.list.column.unimported:`,
			value: ['isImported', '=', false],
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: DepotListEntity;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly depotListSubject = new ReplaySubject<DepotListEntity>(1);

	private readonly loadTriggerSubject = new BehaviorSubject<boolean>(true);

	private readonly optionChangedSubject = new ReplaySubject<OptionChangeAction>(
		1
	);
	private readonly refreshSubject = new ReplaySubject<void>(1);

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	public constructor(
		private readonly store: Store,
		private readonly actions$: Actions,
		private readonly depotSelector: DepotSelector,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'depot-list';
		this.dxLocalStorageKey = DcDxGrids.DepotListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.loadTriggerSubject,
			isLoading: this.depotSelector.getLoading$(),
			list: this.depotListSubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initDepotList());

		this.handleSubscriptions();

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@depot.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.depotSelector
			.getDepotList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				filter((l) => l.availableColumns.size !== 0),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.depotListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();

		// Triggers loading after importing project for the first time
		this.depotListSubject
			.pipe(
				takeUntil(this.optionChangedSubject),
				switchMap((list) =>
					combineAll({
						list,
						count: timer(2000),
					})
				),
				tap(({ list }) => {
					const firstEl = list.elements.first();
					if (firstEl && firstEl.entitiesCount === null) {
						this.refreshSubject.next();
					}
				})
			)
			.subscribe();

		merge(
			this.optionChangedSubject,
			this.refreshSubject.pipe(
				delay(550),
				map(() => 'refresh')
			)
		)
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(520),
				filter(() => this.grid !== null),
				tap((val) => {
					const items = this.grid?.instance
						?.getDataSource()
						?.items() as Array<DepotItemEntity>;
					const depotIds = items.map((it) => it.id);
					this.store.dispatch(
						updateEntitiesCountForDepot({
							ids: depotIds,
							shouldReload: val === 'refresh',
						})
					);
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.loadTriggerSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshDepotList());
		this.refreshSubject.next();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.loadTriggerSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.loadTriggerSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		} else {
			this.refreshSubject.next();
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onOptionChange(e: OptionChangedEvent): void {
		if (!this.grid) {
			return;
		}
		const action: string = e.fullName;
		if (action === 'paging.pageIndex') {
			this.optionChangedSubject.next('page-change');
			return;
		}

		if (action === 'paging.pageSize') {
			this.optionChangedSubject.next('pageSize-change');
			return;
		}

		if (action.includes('filterValue')) {
			this.optionChangedSubject.next('filter');
			return;
		}

		if (action.includes('sortOrder')) {
			this.optionChangedSubject.next('sort');
			return;
		}
	}

	public navigateToDetails(depot: DepotItemEntity): void {
		if (!depot.id) {
			return;
		}

		this.store.dispatch(
			navigateToDepot({
				id: depot.id,
				depotType: depot.type,
			})
		);
	}

	public createNewDepotV2(): void {
		this.store.dispatch(navigateToDepotCreateModal());
	}

	public createNewDepot(): void {
		this.store.dispatch(navigateToDepotCreate());
	}

	public duplicateDepot(depot: DepotItemEntity): void {
		this.store.dispatch(
			navigateToDepotDuplicate({
				id: depot.id,
				depotType: depot.type,
			})
		);
	}

	public deleteDepotRow(event: Event, depot: DepotItemEntity): Promise<void> {
		event.stopPropagation();
		return this.deleteSelected(depot);
	}

	public async createEntityDepot(
		event: Event,
		depot: DepotItemEntity
	): Promise<void> {
		event.stopPropagation();
		const dialogRef = this.modalService.openModal<{
			depotId: number;
			depotType: string;
			depotLabel: string;
		}>(
			CreateEntityFromDepotModalComponent,
			{
				title: $localize`:i18n=@@depot.modal.create.entity.header:`,
				subTitle: '',
				depotId: depot.id,
				depotType: depot.type,
				depotLabel: depot.label,
			},
			{
				disableClose: true,
			}
		);
		const diagData = await firstValueFrom(dialogRef.afterClosed());
		if (!diagData) {
			return this.store.dispatch(resetAvailableHeaders());
		}
		if (diagData.result === 'success') {
			const items = this.grid?.instance
				?.getDataSource()
				?.items() as Array<DepotItemEntity>;
			const depotIds = items.map((it) => it.id);
			this.store.dispatch(
				updateEntitiesCountForDepot({
					ids: depotIds,
					shouldReload: true,
				})
			);
		}
	}

	public toggleActivationStatus(depot: DepotItemEntity): void {
		this.store.dispatch(
			toggleDepotStatus({
				depotId: depot.id,
				depotType: depot.type,
				status: !depot.isActive,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DepotItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DepotItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async deleteSelected(
		selectedItem: DepotItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
			const selectedDepots = await firstValueFrom(
				this.depotSelector.getSelectedDepots$(selectedRowKeys)
			);

			selected = selectedDepots.map((k) => k.id);
			elementLabel = selectedDepots.length === 1 ? selectedDepots[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		this.modalService.openPopup<IDepotActionModalData>(
			DepotActionPopupComponent,
			{
				title: $localize`:i18n=@@depot.action.popup.delete.header:`,
				subTitle: '',
				depotId: selected,
				element: elementLabel,
				action: DepotAction.Delete,
			},
			{
				disableClose: true,
			}
		);
	}

	private handleSubscriptions(): void {
		this.actions$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					deleteDepotSuccess,
					bulkDeleteDepotsSuccess,
					deleteDepotFail,
					bulkDeleteDepotsFail
				),
				tap(() => this.refresh())
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@depot.list.columnSelection:`,
				typed: '',
			},
			{
				order: 2,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@depot.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
